<template>
    <div class="parallax flex flex-col overflow-clip">
        <!-- Breadcrumbs -->
        <BlogArticleBreadcrumbs :article="article" />

        <div class="flex items-center gap-2">
            <!-- Date -->
            <span v-if="article.date" class="text-sm text-gray-900">
                {{ formatDate(article.date) }}
            </span>

            <TransitionGroup
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-300 opacity-300"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-300 opacity-300"
                leave-to-class="transform scale-95 opacity-0"
            >
                <span
                    v-if="readingTime"
                    class="h-2.5 overflow-hidden text-xs text-gray-400"
                >
                    {{ separatorSign }}
                </span>

                <span
                    v-if="readingTime"
                    class="flex items-center gap-1 text-xs"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-4 flex-shrink-0"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                    </svg>

                    <span>{{ readingTime }} {{ t('minutes') }}</span>
                </span>
            </TransitionGroup>
        </div>

        <!-- Title -->
        <h1
            class="mb-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
            :style="{ viewTransitionName: 'title' }"
        >
            {{ article.title }}
        </h1>

        <!-- Description -->
        <p class="not-prose mb-4 text-lg leading-7 text-gray-500">
            {{ article.description }}
        </p>

        <!-- Author -->
        <BlogArticleAuthor :article="article" />
    </div>
</template>

<script setup lang="ts">
import type { BlogArticle } from '~/types/blog'

const separatorSign = '|'

defineProps({
    article: {
        type: Object as PropType<BlogArticle>,
        required: true,
    },
    readingTime: {
        type: Number,
        default: null,
    },
})

const { t } = useI18n()
</script>

<style scoped>
.parallax > * {
    animation: parallax linear;
    animation-timeline: scroll();
}

@keyframes parallax {
    to {
        transform: translateY(2000px);
    }
}
</style>

<i18n lang="yaml">
en:
    minutes: минути
bg:
    minutes: минути
</i18n>
